import type { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';
import { getCourseWithContent, enrollInCourse } from './lms';

const PENDING_FREE_COURSE_KEY = 'lsem-pending-free-course';
export const PENDING_FREE_COURSE_METADATA_KEY = 'pending_course_id';

function readStoredPendingCourse(): string | null {
  try {
    return window.localStorage.getItem(PENDING_FREE_COURSE_KEY);
  } catch {
    return null;
  }
}

function clearStoredPendingCourse() {
  try {
    window.localStorage.removeItem(PENDING_FREE_COURSE_KEY);
  } catch {
    /* ignore */
  }
}

export function rememberPendingFreeCourse(courseId: string) {
  try {
    window.localStorage.setItem(PENDING_FREE_COURSE_KEY, courseId);
  } catch {
    /* storage can be unavailable in private browsing */
  }
}

export function clearPendingFreeCourse() {
  clearStoredPendingCourse();
}

export function getPendingFreeCourse(): string | null {
  return readStoredPendingCourse();
}

export function pendingCourseFromMetadata(user: SupabaseUser | null): string | null {
  const value = user?.user_metadata?.[PENDING_FREE_COURSE_METADATA_KEY];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

async function clearPendingCourseMetadata(userId: string): Promise<void> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.user?.id || data.session.user.id !== userId) return;

  const current = data.session.user.user_metadata?.[PENDING_FREE_COURSE_METADATA_KEY];
  if (!current) return;

  // The metadata travels with the auth user through email confirmation and
  // across browsers, so it is the resilient fallback when localStorage is
  // unavailable or the confirmation link lands on another host.
  await supabase.auth.updateUser({ data: { [PENDING_FREE_COURSE_METADATA_KEY]: null } }).catch(() => null);
}

/**
 * Claim a free-course intent after the account is created/confirmed. The
 * intent may live in auth metadata (survives cross-device confirmation) or in
 * localStorage (legacy/public-catalogue handoff). Course price is always
 * re-checked against the live catalogue before the entitlement is written.
 */
export async function claimPendingFreeCourse(user: SupabaseUser): Promise<string | null> {
  const candidates = Array.from(
    new Set(
      [pendingCourseFromMetadata(user), readStoredPendingCourse()]
        .filter((value): value is string => Boolean(value && value.trim()))
        .map((value) => value.trim()),
    ),
  );
  if (candidates.length === 0) return null;

  let sawTransientFailure = false;

  for (const courseId of candidates) {
    try {
      const course = await getCourseWithContent(courseId);
      const price = Number(course?.price_value ?? NaN);
      if (!course || !course.is_published || price !== 0) {
        continue;
      }

      await enrollInCourse(user.id, courseId);
      clearStoredPendingCourse();
      await clearPendingCourseMetadata(user.id);
      return courseId;
    } catch (error) {
      sawTransientFailure = true;
      console.error(`Could not claim free course ${courseId}; will retry on the next session load.`, error);
    }
  }

  // Only clear intents when we have positively established they are no longer
  // valid. A network/RLS failure must leave the intent in place for a retry.
  if (!sawTransientFailure) {
    clearStoredPendingCourse();
    await clearPendingCourseMetadata(user.id);
  }

  return null;
}
